import React from 'react';
import { Customer, CustomerCategory, Quote, ZohoEstimateSummary, ZohoInvoiceSummary } from '../../types';
import { formatCurrency } from '../../utils/formatCurrency';

interface CommercialTermsModalProps {
  editingCustomer: Customer | null;
  categories: CustomerCategory[];
  editCategoryId: string;
  setEditCategoryId: (value: string) => void;
  applyCategoryDefaults: boolean;
  setApplyCategoryDefaults: (value: boolean) => void;
  editTerms: {
    charterDiscount: number;
    scheduleDiscount: number;
    creditLimit: number;
    paymentPolicyType: NonNullable<Customer['paymentPolicyType']>;
    paymentPolicyLabel: string;
    creditStatus: NonNullable<Customer['creditStatus']>;
    requiresPrepayment: boolean;
    bookingBlockMode: NonNullable<Customer['bookingBlockMode']>;
    paymentPolicyNotes: string;
  };
  setEditTerms: React.Dispatch<
    React.SetStateAction<{
      charterDiscount: number;
      scheduleDiscount: number;
      creditLimit: number;
      paymentPolicyType: NonNullable<Customer['paymentPolicyType']>;
      paymentPolicyLabel: string;
      creditStatus: NonNullable<Customer['creditStatus']>;
      requiresPrepayment: boolean;
      bookingBlockMode: NonNullable<Customer['bookingBlockMode']>;
      paymentPolicyNotes: string;
    }>
  >;
  editCurrency: string;
  isSavingTerms: boolean;
  onCancel: () => void;
  onSave: () => void;
  editingQuotes: Quote[];
  zohoFinance: { invoices: ZohoInvoiceSummary; estimates: ZohoEstimateSummary } | null;
}

const CommercialTermsModal: React.FC<CommercialTermsModalProps> = ({
  editingCustomer,
  categories,
  editCategoryId,
  setEditCategoryId,
  applyCategoryDefaults,
  setApplyCategoryDefaults,
  editTerms,
  setEditTerms,
  editCurrency,
  isSavingTerms,
  onCancel,
  onSave,
  editingQuotes,
  zohoFinance,
}) => {
  if (!editingCustomer) return null;

  const zohoTermsLabel = editingCustomer.zohoPaymentTermsLabel?.trim() || 'Not available';
  const zohoTermsDays =
    typeof editingCustomer.zohoPaymentTermsDays === 'number' && Number.isFinite(editingCustomer.zohoPaymentTermsDays)
      ? editingCustomer.zohoPaymentTermsDays
      : null;

  const paymentPolicyOptions: Array<{ value: NonNullable<Customer['paymentPolicyType']>; label: string }> = [
    { value: 'UNKNOWN', label: 'Unknown' },
    { value: 'CREDIT_7_DAYS', label: 'Credit 7 Days' },
    { value: 'CREDIT_BY_7_NEXT_MONTH', label: 'Credit by 7 Next Month' },
    { value: 'CREDIT_BY_15_NEXT_MONTH', label: 'Credit by 15 Next Month' },
    { value: 'CREDIT_BY_30_NEXT_MONTH', label: 'Credit by 30 Next Month' },
    { value: 'PREPAYMENT_REQUIRED', label: 'Prepayment Required' },
  ];

  const creditStatusOptions: Array<{ value: NonNullable<Customer['creditStatus']>; label: string }> = [
    { value: 'UNKNOWN', label: 'Unknown' },
    { value: 'ACTIVE', label: 'Active' },
    { value: 'ON_HOLD', label: 'On Hold' },
    { value: 'BLOCKED', label: 'Blocked' },
  ];

  const bookingBlockModeOptions: Array<{ value: NonNullable<Customer['bookingBlockMode']>; label: string }> = [
    { value: 'NONE', label: 'None' },
    { value: 'WARN_ONLY', label: 'Warn Only' },
    { value: 'BLOCK_VOUCHER', label: 'Block Voucher' },
    { value: 'BLOCK_BOOKING', label: 'Block Booking' },
  ];

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-6 z-50">
      <div className="bg-white rounded-[2rem] border border-slate-100 shadow-2xl max-w-xl w-full overflow-hidden">
        <div className="px-8 py-6 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-black text-slate-800 uppercase tracking-widest">Adjust Commercial Terms</h3>
            <p className="text-xs text-slate-400 mt-1">{editingCustomer.name}</p>
          </div>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-600 text-2xl leading-none" aria-label="Close">
            ×
          </button>
        </div>
        <div className="p-8 space-y-6">
          <div className="space-y-3">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Discount Group</label>
            <div className="flex items-center gap-3">
              <select
                value={editCategoryId}
                onChange={(e) => setEditCategoryId(e.target.value)}
                className="w-full bg-slate-50 border-none rounded-xl p-4 font-bold text-slate-800"
              >
                <option value="">No Group (Manual Terms)</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
              {editCategoryId && (
                <button
                  onClick={() => {
                    if (
                      confirm(
                        'Remove discount group from this customer? This will NOT change current discounts unless you apply defaults.'
                      )
                    ) {
                      setEditCategoryId('');
                      setApplyCategoryDefaults(false);
                    }
                  }}
                  className="px-4 py-3 text-[10px] font-black uppercase rounded-xl border border-rose-200 text-rose-600 hover:bg-rose-50"
                >
                  Remove
                </button>
              )}
            </div>
            {editCategoryId && (
              <label className="flex items-center gap-2 text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                <input
                  type="checkbox"
                  checked={applyCategoryDefaults}
                  onChange={(e) => setApplyCategoryDefaults(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300"
                />
                Apply group defaults on save
              </label>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Charter Disc %</label>
              <input
                type="number"
                min="0"
                max="100"
                className="w-full bg-slate-50 border-none rounded-xl p-4 font-bold text-slate-800"
                value={editTerms.charterDiscount}
                onChange={(e) => setEditTerms((prev) => ({ ...prev, charterDiscount: parseFloat(e.target.value) || 0 }))}
                disabled={applyCategoryDefaults}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Schedule Disc %</label>
              <input
                type="number"
                min="0"
                max="100"
                className="w-full bg-slate-50 border-none rounded-xl p-4 font-bold text-slate-800"
                value={editTerms.scheduleDiscount}
                onChange={(e) => setEditTerms((prev) => ({ ...prev, scheduleDiscount: parseFloat(e.target.value) || 0 }))}
                disabled={applyCategoryDefaults}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Credit Limit ({editCurrency})</label>
              <input
                type="number"
                min="0"
                className="w-full bg-slate-50 border-none rounded-xl p-4 font-bold text-slate-800"
                value={editTerms.creditLimit}
                onChange={(e) => setEditTerms((prev) => ({ ...prev, creditLimit: parseFloat(e.target.value) || 0 }))}
                disabled={applyCategoryDefaults}
              />
            </div>
          </div>
          <div className="bg-slate-50 rounded-xl p-4 text-xs text-slate-500">
            Discounts are customer-specific overrides. If you want category defaults, use the Commercial Terms tab in System
            Registry.
          </div>

          <div className="bg-emerald-50/60 border border-emerald-100 rounded-xl p-4 space-y-2">
            <p className="text-[10px] font-black text-emerald-700 uppercase tracking-widest">Zoho Books Terms</p>
            <p className="text-sm font-black text-slate-800">
              {zohoTermsLabel}
              {zohoTermsDays !== null ? ` / ${zohoTermsDays} days` : ''}
            </p>
            <p className="text-xs text-slate-500">Reference only. ME-OS operational policy is managed separately.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Payment Policy</label>
              <select
                value={editTerms.paymentPolicyType}
                onChange={(e) =>
                  setEditTerms((prev) => ({
                    ...prev,
                    paymentPolicyType: e.target.value as NonNullable<Customer['paymentPolicyType']>,
                    requiresPrepayment:
                      e.target.value === 'PREPAYMENT_REQUIRED' ? true : prev.requiresPrepayment,
                  }))
                }
                className="w-full bg-slate-50 border-none rounded-xl p-4 font-bold text-slate-800"
              >
                {paymentPolicyOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Payment Label</label>
              <input
                type="text"
                className="w-full bg-slate-50 border-none rounded-xl p-4 font-bold text-slate-800"
                value={editTerms.paymentPolicyLabel}
                onChange={(e) => setEditTerms((prev) => ({ ...prev, paymentPolicyLabel: e.target.value }))}
                placeholder="Optional operator-facing override"
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Credit Status</label>
              <select
                value={editTerms.creditStatus}
                onChange={(e) =>
                  setEditTerms((prev) => ({
                    ...prev,
                    creditStatus: e.target.value as NonNullable<Customer['creditStatus']>,
                  }))
                }
                className="w-full bg-slate-50 border-none rounded-xl p-4 font-bold text-slate-800"
              >
                {creditStatusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Booking Block Mode</label>
              <select
                value={editTerms.bookingBlockMode}
                onChange={(e) =>
                  setEditTerms((prev) => ({
                    ...prev,
                    bookingBlockMode: e.target.value as NonNullable<Customer['bookingBlockMode']>,
                  }))
                }
                className="w-full bg-slate-50 border-none rounded-xl p-4 font-bold text-slate-800"
              >
                {bookingBlockModeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <label className="flex items-center gap-2 text-[10px] font-bold text-slate-500 uppercase tracking-widest">
            <input
              type="checkbox"
              checked={editTerms.requiresPrepayment}
              onChange={(e) => setEditTerms((prev) => ({ ...prev, requiresPrepayment: e.target.checked }))}
              className="w-4 h-4 rounded border-slate-300"
            />
            Requires prepayment
          </label>

          <div className="space-y-2">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Payment Policy Notes</label>
            <textarea
              className="w-full min-h-[96px] bg-slate-50 border-none rounded-xl p-4 font-medium text-slate-800"
              value={editTerms.paymentPolicyNotes}
              onChange={(e) => setEditTerms((prev) => ({ ...prev, paymentPolicyNotes: e.target.value }))}
              placeholder="Internal finance guidance for Contact Center operators"
            />
          </div>

          {editingCustomer?.source === 'ZOHO' && editingCustomer?.zohoContactId && (
            <div className="bg-white border border-slate-100 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Zoho Finance</p>
              </div>
              {zohoFinance ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Invoices</p>
                    <p className="mt-2 font-bold text-slate-700">Count: {zohoFinance.invoices?.count || 0}</p>
                    <p className="font-bold text-slate-700">
                      Total: {formatCurrency(Number(zohoFinance.invoices?.total || 0), zohoFinance.invoices?.currencyCode || editCurrency)}
                    </p>
                    <p className="font-bold text-rose-600">
                      Outstanding:{' '}
                      {formatCurrency(Number(zohoFinance.invoices?.balance || 0), zohoFinance.invoices?.currencyCode || editCurrency)}
                    </p>
                    {zohoFinance.invoices?.last?.date && (
                      <p className="mt-2 text-[10px] text-slate-500">
                        Last: {new Date(zohoFinance.invoices.last.date).toLocaleDateString()} ({zohoFinance.invoices.last.status})
                      </p>
                    )}
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Estimates</p>
                    <p className="mt-2 font-bold text-slate-700">Count: {zohoFinance.estimates?.count || 0}</p>
                    <p className="font-bold text-slate-700">
                      Total: {formatCurrency(Number(zohoFinance.estimates?.total || 0), zohoFinance.estimates?.currencyCode || editCurrency)}
                    </p>
                    {zohoFinance.estimates?.last?.date && (
                      <p className="mt-2 text-[10px] text-slate-500">
                        Last: {new Date(zohoFinance.estimates.last.date).toLocaleDateString()} ({zohoFinance.estimates.last.status})
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-xs text-slate-400">Use “Refresh Live” on the customer card to pull Zoho finance.</p>
              )}
            </div>
          )}

          {editingQuotes && (
            <div className="bg-white border border-slate-100 rounded-xl p-4">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Recent Quotes</p>
              {editingQuotes.length ? (
                <div className="space-y-2">
                  {editingQuotes.slice(0, 3).map((q) => (
                    <div key={q.id} className="flex items-center justify-between text-xs">
                      <span className="font-bold text-slate-700">{new Date(q.date).toLocaleDateString()}</span>
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{q.status}</span>
                      <span className="font-black text-slate-800">
                        {formatCurrency(Number(q.totalUSD || 0), editCurrency)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400">No quotes yet.</p>
              )}
            </div>
          )}
        </div>
        <div className="px-8 py-6 border-t border-slate-100 flex justify-end gap-4">
          <button onClick={onCancel} className="px-6 py-2 text-[10px] font-black uppercase text-slate-400 hover:text-slate-600">
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={isSavingTerms}
            className={`px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest ${
              isSavingTerms ? 'bg-slate-200 text-slate-500 cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-700'
            }`}
          >
            {isSavingTerms ? 'Saving...' : 'Save Terms'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommercialTermsModal;
